import { Controller } from "@hotwired/stimulus"

// The dressing room's mirrors.
//
// Colours are CSS variables all the way down, so trying one on is writing a
// handful of custom properties; the values arrive precomputed from the server
// (the same Genetics::Color that renders every room) and this controller owns
// no colour math at all.
//
// Anything with a shape — a hairstyle, a helm, a bonnet on the dragon — is
// redrawn by the server: the current form is sent to the preview endpoint and
// the mirror's contents swapped for the answer. Nothing is saved until the
// form's own submit.
//
// Three looks share the page — the keeper's, the dragon's, the horse's —
// and one form, so one save keeps all three. Each look has its own
// parameter prefix, its own glass and its own pane; one is shown at a time,
// under a mirror strip that stays at the top of the screen, so the piece
// tapped is seen on the figure without scrolling back up. The pane shown is
// kept in the address (#attire, #trappings, #tack — or any slot inside one,
// which is where a purchase sends the keeper back to).
const FORM = "look"
const PREFIXES = { keeper: "wardrobe", dragon: "dragon", horse: "horse" }
const PANE_OF = { attire: "keeper", colour: "keeper", trappings: "dragon", tack: "horse" }

export default class extends Controller {
  static targets = ["preview", "dragonPreview", "horsePreview", "announcer", "card",
    "glass", "tab", "pane", "mirror"]
  static values = { previewUrl: String, at: String }

  connect() {
    this.sequence = { keeper: 0, dragon: 0, horse: 0 }
    this.onHash = () => this.openFromHash({ scroll: true })
    window.addEventListener("hashchange", this.onHash)
    this.measureGlass()
    if (this.hasGlassTarget && "ResizeObserver" in window) {
      this.sizer = new ResizeObserver(() => this.measureGlass())
      this.sizer.observe(this.glassTarget)
    }
    // A purchase comes back with ?at=trappings-neck (a redirect's fragment
    // is lost to Turbo's fetch); it becomes the address's hash and is
    // opened like one.
    if (this.atValue) {
      const url = new URL(location.href)
      url.searchParams.delete("at")
      url.hash = this.atValue
      history.replaceState(history.state, "", url)
    }
    this.openFromHash({ scroll: true })
  }

  disconnect() {
    window.removeEventListener("hashchange", this.onHash)
    this.sizer?.disconnect()
  }

  // The strip's height, told to the stylesheet: every slot's scroll margin
  // is it, so a slot scrolled to (by the address, by Turbo, by us) lands
  // under the glass and not behind it.
  measureGlass() {
    if (!this.hasGlassTarget) return
    this.element.style.setProperty("--ll-glass-h", `${this.glassTarget.offsetHeight}px`)
  }

  // ---------------------------------------------------------------- the tabs

  // A tab tapped: its pane and its mirror, and the address remembers.
  show(event) {
    const who = event.params.who
    this.open(who)
    const pane = this.paneFor(who)
    if (pane) history.replaceState(history.state, "", `#${pane.id}`)
    window.scrollTo({ top: 0, behavior: "instant" })
  }

  open(who) {
    if (!PREFIXES[who]) who = "keeper"
    for (const tab of this.tabTargets) {
      const on = tab.dataset.who === who
      tab.setAttribute("aria-selected", on ? "true" : "false")
      tab.classList.toggle("ll-wardrobe__tab--on", on)
    }
    for (const pane of this.paneTargets) pane.hidden = pane.dataset.who !== who
    for (const mirror of this.mirrorTargets) mirror.hidden = mirror.dataset.who !== who
    this.who = who
  }

  // The address names a pane (#trappings) or a slot in one (#trappings-head,
  // #colour-hair): open that pane, and bring the slot up under the glass.
  openFromHash({ scroll }) {
    const hash = decodeURIComponent(location.hash.replace(/^#/, ""))
    const who = PANE_OF[hash.split("-")[0]] || "keeper"
    this.open(who)
    if (!scroll || !hash) return

    const slot = document.getElementById(hash)
    if (!slot || slot.matches("[data-wardrobe-target~='pane']")) {
      window.scrollTo({ top: 0, behavior: "instant" })
      return
    }
    // After layout, so the slot has a box now its pane is shown; its scroll
    // margin (measureGlass) keeps it out from under the strip.
    requestAnimationFrame(() => slot.scrollIntoView({ block: "start", behavior: "instant" }))
  }

  paneFor(who) {
    return this.paneTargets.find((pane) => pane.dataset.who === who)
  }

  // -------------------------------------------------------------- the mirror

  // A colour swatch: instant, client-side.
  don(event) {
    this.clearTrying()
    this.paint(event.params.vars)
    this.announce(event.params.slot, event.params.name)
  }

  // A dye not yet bought: the same instant restyle, but nothing in the form
  // changes, so the saved look stays what the radios say.
  tryColour(event) {
    this.clearTrying()
    this.paint(event.params.vars)
    const card = event.currentTarget.closest(".ll-dye")
    if (card) card.classList.add("ll-dye--trying")
    this.announce("Trying on", event.params.name)
  }

  paint(vars) {
    const root = this.previewTarget.querySelector(".ll-keeper-root")
    if (!root) return
    for (const [ property, value ] of Object.entries(vars)) {
      root.style.setProperty(property, value)
    }
  }

  // The colours the form actually says, reapplied — so a dye tried on and
  // not bought comes off when the next choice is made.
  restoreColours() {
    const form = document.getElementById(FORM)
    if (!form) return
    for (const input of form.elements) {
      if (input.type === "radio" && input.checked && input.dataset.wardrobeVarsParam) {
        this.paint(JSON.parse(input.dataset.wardrobeVarsParam))
      }
    }
  }

  // A cut or a piece the keeper may wear: redraw the keeper.
  fit(event) {
    this.clearTrying()
    this.announce(event.params.slot, event.params.name)
    this.redraw("keeper")
  }

  // Something for the dragon: redraw the dragon.
  dress(event) {
    this.clearTrying()
    this.announce(event.params.slot, event.params.name)
    this.redraw("dragon")
  }

  // Tack for the horse: redraw the horse, rider and all.
  saddle(event) {
    this.clearTrying()
    this.announce(event.params.slot, event.params.name)
    this.redraw("horse")
  }

  // A piece off the rack, not owned: shown in the mirror over the current
  // choices, until the next choice replaces it.
  tryOn(event) {
    event.stopPropagation()
    this.tryPiece(event.params, event.currentTarget.closest(".ll-rack-card"))
  }

  // The whole card is the button, on a phone especially; its own buttons
  // and links keep their meaning.
  tryCard(event) {
    if (event.target.closest("button, a, form, input, summary")) return
    this.tryPiece(event.params, event.currentTarget)
  }

  tryPiece({ who, slot, key, name }, card) {
    this.clearTrying()
    if (card) card.classList.add("ll-rack-card--trying")
    this.announce("Trying on", name)
    this.redraw(who, { [slot]: key })
  }

  async redraw(who, override = {}) {
    const form = document.getElementById(FORM)
    const target = this.mirrorFor(who)
    if (!form || !target) return

    const prefix = PREFIXES[who] || PREFIXES.keeper
    const params = new URLSearchParams()
    for (const [ name, value ] of new FormData(form)) {
      if (name.startsWith(`${prefix}[`)) params.append(name, value)
    }
    for (const [ slot, key ] of Object.entries(override)) {
      params.set(`${prefix}[${slot}]`, key)
    }

    const sequence = ++this.sequence[who]
    const response = await fetch(`${this.previewUrlValue}?${params}`, {
      headers: { Accept: "text/html" }
    })
    if (!response.ok || sequence !== this.sequence[who]) return

    target.innerHTML = await response.text()
  }

  mirrorFor(who) {
    if (who === "dragon") return this.hasDragonPreviewTarget ? this.dragonPreviewTarget : null
    if (who === "horse") return this.hasHorsePreviewTarget ? this.horsePreviewTarget : null
    return this.hasPreviewTarget ? this.previewTarget : null
  }

  clearTrying() {
    const trying = this.cardTargets.some((card) => card.matches(".ll-rack-card--trying, .ll-dye--trying"))
    for (const card of this.cardTargets) card.classList.remove("ll-rack-card--trying", "ll-dye--trying")
    if (trying) this.restoreColours()
  }

  announce(slot, name) {
    if (!this.hasAnnouncerTarget) return
    this.announcerTarget.textContent = `${slot || "Colour"}: ${name || "chosen"}.`
  }
}
